import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Plus, Trash2, ArrowRight, MessageSquare } from 'lucide-react';
import { api } from '@/lib/api';

export default function Dashboard() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const res = await api.get('/trips');
      const tripList = res.data?.trips || (Array.isArray(res.data) ? res.data : []);
      setTrips(tripList);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login');
      }
      console.error('Errore caricamento viaggi:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, tripId) => {
    e.stopPropagation();
    if (!window.confirm('Sei sicuro di voler eliminare questo viaggio?')) return;

    try {
      await api.delete(`/trips/${tripId}`);
      setTrips(trips.filter((t) => (t.id || t._id) !== tripId));
    } catch (err) {
      console.error('Errore eliminazione:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1437] text-white px-4 py-8 md:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">Il Genio in Vacanza</p>
            <h1 className="text-3xl md:text-4xl font-serif mt-1 text-[#ede7d8]">I tuoi viaggi</h1>
            <p className="text-sm text-gray-400 mt-1">Ogni destinazione, ogni giornata, sempre a portata di mano.</p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/feedback"
              className="flex items-center gap-2 px-4 py-2 border border-gray-700 hover:border-[#d4af37] rounded-lg text-sm text-gray-300 hover:text-white transition"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Feedback</span>
            </Link>
            <Link
              to="/plan"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#d4af37] hover:bg-[#b79a5a] text-[#0b1437] font-semibold rounded-full text-sm transition shadow-lg"
            >
              <Plus className="w-4 h-4" />
              <span>Nuovo viaggio</span>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Caricamento dei tuoi itinerari...</div>
        ) : trips.length === 0 ? (
          <div className="text-center py-16 bg-[#12204f]/40 border border-gray-800 rounded-2xl p-8">
            <MapPin className="w-12 h-12 text-[#d4af37] mx-auto mb-3 opacity-80" />
            <h3 className="text-xl font-serif text-[#ede7d8] mb-2">Nessun viaggio salvato</h3>
            <p className="text-sm text-gray-400 mb-6">Non hai ancora pianificato nessun viaggio. Chiedi al Genio!</p>
            <Link
              to="/plan"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#d4af37] text-[#0b1437] font-semibold rounded-full text-sm transition"
            >
              Pianifica ora
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trips.map((trip) => (
              <div
                key={trip.id || trip._id}
                onClick={() => navigate(`/trip/${trip.id || trip._id}`)}
                className="group cursor-pointer bg-[#12204f]/60 hover:bg-[#12204f] border border-gray-800 hover:border-[#d4af37]/50 rounded-xl p-5 transition-all duration-300 relative flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between text-xs text-[#d4af37] mb-3">
                    <span className="flex items-center gap-1 font-mono uppercase">
                      <Calendar className="w-3.5 h-3.5" />
                      {trip.departure_date} - {trip.return_date}
                    </span>
                    <button
                      onClick={(e) => handleDelete(e, trip.id || trip._id)}
                      className="text-gray-500 hover:text-red-400 p-1 transition"
                      title="Elimina viaggio"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <h2 className="text-xl font-serif text-[#ede7d8] group-hover:text-white transition mb-3">
                    {trip.title || `Viaggio a ${trip.destination}`}
                  </h2>

                  <div className="space-y-1.5 text-xs text-gray-300 mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      <span className="capitalize">{trip.destination}</span>
                    </div>
                    {trip.people && (
                      <div className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        <span>{trip.people} persone</span>
                        {trip.budget && (
                          <span className="text-gray-400"> - {trip.budget} {trip.currency || 'EUR'}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-800/60 flex items-center justify-between text-xs text-[#d4af37] font-medium group-hover:translate-x-1 transition-transform">
                  <span>Apri</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
